import React, { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../components/user/BCPAuthProvider";
import { useParams } from "react-router-dom";
import { Box, Container, Link, Typography, useTheme } from "@mui/material";

import {
  findGetParameter,
  getData
} from "../../components/utilities/UtilityFunctions";

import makeStyles from "@mui/styles/makeStyles";
import RankingsWidget from "../../components/rankings/RankingsWidget";
import ModalLoadingFull from "../../components/modals/ModalLoadingFull";
import {
  getRegionName,
  handlePagingAll
} from "../../components/api/APIUtilities";
import { postNotification } from "../../components/utilities/Notifications";
import { getEnvironmentVariable } from "../../global/environmentVariables";
const useStyles = makeStyles(() => ({
  image: {
    maxHeight: "300px",
    maxWidth: "90%"
  }
}));

export default function TruncatedRankings(props) {
  let { leagueId } = useParams();
  const { regionId, appRoute } = props;
  const regionIdParam = findGetParameter("region");
  const regionName = getRegionName(regionId || regionIdParam);

  const authContext = useContext(AuthContext);
  const embed = appRoute || findGetParameter("embed") === "true";
  const theme = useTheme();
  const classes = useStyles();

  const [loading, setLoading] = useState(true);
  const [rankingGroups, setRankingGroups] = useState(null);
  const [factionRankingGroups, setFactionRankingGroups] = useState(null);
  const [league, setLeague] = useState(null);
  const [hidePlacings, setHidePlacings] = useState(false);
  const [factionLabel, setFactionLabel] = useState(null);

  useEffect(() => {
    const go = async () => {
      try {
        var apiLeague = await getData(
          `/leagues/${leagueId || props.leagueId}`,
          {
            queryStringParameters: {
              "expand[]": ["gameSystem", "gameStore", "gameStoreUser"]
            }
          }
        );

        setFactionLabel(apiLeague?.gameSystem?.factionLabel || "Faction");

        if (apiLeague.photoFileId) {
          apiLeague.photo = await getData(`/files/${apiLeague.photoFileId}`, {
            queryStringParameters: {}
          });
        }

        setLeague(apiLeague);
        if (
          apiLeague?.hidePlacings &&
          authContext?.authState?.identity?.isAdmin !== true &&
          !authContext?.authState?.identity?.adminGameSystems?.includes(
            apiLeague?.gameSystemId
          )
        ) {
          if (
            !apiLeague.gameStoreUser?.active ||
            (!apiLeague.gameStoreUser?.owner &&
              !apiLeague.gameStoreUser?.permissions?.createLeagues)
          ) {
            let error = new Error(
              "The Circuit owner currently has Rankings hidden."
            );
            error.name = "HidePlacings Error";
            throw error;
          }
        }

        var aRankingGroups = [
          {
            ...apiLeague,
            name: `${apiLeague.name} Rankings`,
            topX: 10
          }
        ];

        if (
          apiLeague.gameSystem?.allowsTeamEvent &&
          apiLeague.defaultPlacingType !== "ELO Points"
        ) {
          aRankingGroups.push({
            ...apiLeague,
            name: "Team Rankings",
            topX: 5,
            teams: true
          });
        }

        setRankingGroups(aRankingGroups);
      } catch (err) {
        if (err.name === "HidePlacings Error") {
          postNotification("warning", err.message);
          setHidePlacings(true);
        } else {
          postNotification("error", err);
        }
        setLoading(false);
        return;
      }
      setLoading(false);

      if (
        apiLeague.gameSystem?.hasFactions &&
        apiLeague.defaultPlacingType !== "ELO Points"
      ) {
        var apiFactions = await handlePagingAll(`/armies`, {
          limit: 100,
          gameType: apiLeague.gameSystem.code,
          [apiLeague.itc ? "itcFaction" : "leagueFaction"]: true
        });

        apiFactions.sort((a, b) => {
          if (a.rankingSortOrder !== b.rankingSortOrder) {
            if (a.rankingSortOrder === undefined) return 1;
            if (b.rankingSortOrder === undefined) return -1;
            if (a.rankingSortOrder < b.rankingSortOrder) return -1;
            if (a.rankingSortOrder > b.rankingSortOrder) return 1;
          }

          if (!a.name && !b.name) return 0;
          if (!a.name) return -1;
          if (!b.name) return 1;

          var aVal = a.name.toUpperCase();
          var bVal = b.name.toUpperCase();

          if (aVal < bVal) return -1;
          if (aVal > bVal) return 1;
          return 0;
        });

        var delay = 0;
        apiFactions = apiFactions.map((rankingGroup) => {
          delay += 0.1;
          return {
            ...apiLeague,
            ...rankingGroup,
            topX: rankingGroup.topX || 3,
            delay: delay
          };
        });

        setFactionRankingGroups(apiFactions);
      }
    };

    go();
    // eslint-disable-next-line
  }, []);

  return (
    <>
      {loading && <ModalLoadingFull />}
      {!loading && (
        <Container
          sx={{
            pt: 2,
            pb: 2,
            textAlign: "center"
          }}
          maxWidth="lg"
        >
          {hidePlacings ? (
            <Box>
              <img
                src={
                  league?.photo?.url ||
                  league?.imageUrl ||
                  `${getEnvironmentVariable(
                    "SYSTEM_IMAGES_CLOUDFRONT"
                  )}/images/RankingsImage${
                    theme?.palette?.mode === "dark" ? "Dark" : ""
                  }.png`
                }
                alt={league?.name}
                className={classes.image}
              />

              <Typography variant="h2" gutterBottom>
                {league?.name}
                {regionName ? ` - ${regionName}` : ""}
              </Typography>
              <Typography variant="body1" paragraph>
                Powered by{" "}
                <Link
                  target="_blank"
                  href={`${window.location.protocol}//${window.location.host}`}
                  underline="none"
                >
                  {getEnvironmentVariable("COMPANY_NAME")}
                </Link>
              </Typography>
              <Typography variant="h4" gutterBottom>
                The Circuit owner currently has Rankings hidden
              </Typography>
            </Box>
          ) : (
            <>
              <RankingsWidget
                rankingGroups={rankingGroups}
                leagueId={leagueId || props.leagueId}
                regionId={regionId || regionIdParam}
                embed={embed}
              />
              {factionRankingGroups && (
                <>
                  <Typography variant="h3" gutterBottom>
                    {factionLabel} Rankings
                  </Typography>
                  <RankingsWidget
                    rankingGroups={factionRankingGroups}
                    leagueId={leagueId || props.leagueId}
                    regionId={regionId || regionIdParam}
                    isArmy
                    embed={embed}
                  />
                </>
              )}
            </>
          )}
        </Container>
      )}
    </>
  );
}
